import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Code, Shield, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DevDashboard() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);

  const loadUser = useCallback(async () => {
    try {
      const { data: { session } } = await digitalOcean.auth.getSession();
      
      if (!session) {
        navigate("/Auth");
        return;
      }

      const { data: profile, error: profileError } = await digitalOcean
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      if (profileError) {
        console.error("Error loading profile:", profileError);
        setError("Failed to load user profile. Please try refreshing the page.");
        return;
      }

      if (!profile) {
        console.error("Profile not found for user:", session.user.id);
        setError("User profile not found. Please contact support.");
        return;
      }

      setCurrentUser(profile);

      const { data: roles, error: rolesError } = await digitalOcean
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      if (rolesError) {
        console.error("Error loading user roles:", rolesError);
        setIsAdmin(false);
        return;
      }

      const hasAdminAccess = roles?.some(r => r.role === 'admin');
      setIsAdmin(hasAdminAccess);
    } catch (err) {
      console.error("Unexpected error in loadUser:", err);
      setError("An unexpected error occurred. Please try refreshing the page.");
    }
  }, [navigate]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const { data: stats } = useQuery({
    queryKey: ["dev-stats"],
    queryFn: async () => {
      const [
        { count: totalUsers },
        { count: totalTracks },
        { count: totalFeedback },
        { count: totalVotes }
      ] = await Promise.all([
        digitalOcean.from("profiles").select("*", { count: "exact", head: true }),
        digitalOcean.from("tracks").select("*", { count: "exact", head: true }),
        digitalOcean.from("feedback").select("*", { count: "exact", head: true }),
        digitalOcean.from("votes").select("*", { count: "exact", head: true }),
      ]);

      return { totalUsers, totalTracks, totalFeedback, totalVotes };
    },
    enabled: isAdmin,
  });

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">Error Loading Dashboard</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => window.location.reload()} tooltip="Reload the page">
            Reload Page
          </Button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen p-8">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You need admin privileges to access the developer dashboard.
          </p>
          <Button onClick={() => navigate("/Discover")} tooltip="Return to the discover page">Back to Discover</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-8 flex items-center gap-3">
          <Code className="w-8 h-8" />
          Developer Dashboard
        </h1>

        {stats && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-muted-foreground mb-2">Total Users</h3>
              <div className="text-3xl font-bold text-foreground">{stats.totalUsers || 0}</div>
            </div>
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-muted-foreground mb-2">Total Tracks</h3>
              <div className="text-3xl font-bold text-foreground">{stats.totalTracks || 0}</div>
            </div>
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-muted-foreground mb-2">Total Feedback</h3>
              <div className="text-3xl font-bold text-foreground">{stats.totalFeedback || 0}</div>
            </div>
            <div className="bg-card p-6 rounded-lg border border-border">
              <h3 className="text-muted-foreground mb-2">Total Votes</h3>
              <div className="text-3xl font-bold text-foreground">{stats.totalVotes || 0}</div>
            </div>
          </div>
        )}

        <div className="bg-card p-8 rounded-lg border border-border">
          <h2 className="text-xl font-semibold text-card-foreground mb-4">System Status</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Database</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded text-sm">Operational</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Authentication</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded text-sm">Operational</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Storage</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded text-sm">Operational</span>
            </div>
          </div>
        </div>

        <div className="bg-destructive/10 p-8 rounded-lg border border-destructive mt-8">
          <h2 className="text-xl font-semibold text-destructive mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Danger Zone
          </h2>
          <div className="space-y-4">
            <div>
              <p className="text-muted-foreground mb-3">
                Reset ALL user passwords to their email addresses. Users will need to log in with their email as password.
              </p>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (!confirm("Are you sure? This will reset EVERY user's password to their email address!")) return;
                  if (!confirm("FINAL WARNING: This action cannot be undone. Continue?")) return;
                  
                  toast.loading("Resetting passwords...");
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const response = await fetch(
                      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-all-passwords`,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${session?.access_token}`,
                        },
                      }
                    );
                    const result = await response.json();
                    toast.dismiss();
                    if (result.success) {
                      toast.success(result.message);
                    } else {
                      toast.error(result.error || "Failed to reset passwords");
                    }
                  } catch (err) {
                    toast.dismiss();
                    toast.error("Failed to reset passwords");
                  }
                }}
              >
                Reset All Passwords to Email
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
